import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Property } from "@/types/property";
import { Edit, Trash } from "lucide-react";
import { useState } from "react";
import DeleteDialog from "../shared/DeleteDialog";
import { usePropertyMutations } from "@/hooks/usePropertyMutations";
import { formatISOToDDMMYY } from "@/utils/dateFormatter";

interface PropertyRowProps {
  property: Property;
  onEdit: (property: Property) => void;
  canDelete: boolean;
}

const PropertyRow = ({ property, onEdit, canDelete }: PropertyRowProps) => {
  const { deleteProperty } = usePropertyMutations();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProperty.mutateAsync(property.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatCharacteristics = () => {
    return `${property.guests} ${property.guests === 1 ? 'persona' : 'personas'} | ${property.bedrooms} hab | ${property.bathrooms} ${property.bathrooms === 1 ? 'baño' : 'baños'}`;
  };

  return (
    <TableRow>
      <TableCell className="font-medium">
        {property.shortName || 'N/A'}
      </TableCell>
      
      <TableCell>
        {property.operationStartedAt ? formatISOToDDMMYY(property.operationStartedAt) : 'N/A'}
      </TableCell>
      
      <TableCell className="max-w-xs">
        <div className="line-clamp-2 leading-tight">
          {property.title}
        </div>
      </TableCell>
      
      <TableCell>
        <Badge variant={property.available ? "default" : "secondary"}>
          {property.available ? "Disponible" : "No disponible"}
        </Badge>
      </TableCell>
      
      <TableCell>
        <Badge variant={property.featured ? "default" : "outline"}>
          {property.featured ? "Sí" : "No"}
        </Badge>
      </TableCell>
      
      <TableCell>
        {formatPrice(property.price)}
      </TableCell>
      
      <TableCell>
        {formatCharacteristics()}
      </TableCell>
      
      <TableCell className="max-w-xs">
        <div className="truncate">
          {property.location}
        </div>
      </TableCell>
      
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(property)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          {canDelete && (
            <DeleteDialog
              title="Eliminar propiedad"
              description={`¿Estás seguro de que deseas eliminar la propiedad "${property.title}"? Esta acción no se puede deshacer.`}
              onConfirm={handleDelete}
            >
              <Button
                variant="destructive"
                size="sm"
                disabled={isDeleting}
              >
                <Trash className="h-4 w-4" />
              </Button>
            </DeleteDialog>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default PropertyRow;